import React, { useEffect, useRef, useState } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  deleteSigner,
  removeFieldsOfCC,
  setContacts,
  setErrors,
  updateIndividualSigner,
} from "redux/appSlice";
import {
  DeliveryChannel,
  DeliveryMode,
  RequestParticipant,
  RequestSecurity,
} from "models/interfaces";
import { isTruthy, convertESTtoUserLocalDateAndTime } from "helpers/methods";
import { useFeatureState } from "hooks/use-feature";
import { useIdvSecurityOptions } from "hooks/use-idv-security-options";
import {
  idvMethodsFor,
  idvSecurityKeyOf,
  idvSecurityOption,
  isIdvSecurityKey,
} from "helpers/identityVerificationHelpers";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import { CopyGlyph, ShieldGlyph, TickGlyph } from "../../components/RecipientsIcons";
import { getContactSuggestion } from "../../SignRequestHelper";
import {
  getParticipantNameError,
  isValidParticipantNameLength,
} from "screens/Shared/CommonHelper";
import InPersonHandoffDialog from "screens/InPersonSigning/InPersonHandoffDialog";
import { useRequesterInPersonConsent } from "screens/InPersonSigning/useRequesterInPersonConsent";
import MobileTextField from "./MobileTextField";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";
import { embedDeliveryModeOf, isEmbedEditorMode } from "embed/EmbedEditorMode";

const ItemType = { SIGNER: "signer" };

const ROLE_NEED_TO_SIGN = "Need to sign";
const ROLE_RECEIVES_COPY = "Receives a copy";
const SEC_NONE = "No security";
const SEC_CODE = "Security code";
const SEC_OTP = "OTP verification";
/** SMS OTP — the phone-authentication option, desktop's `SECURITY.phone`. */
const SEC_PHONE = strings.smsDelivery.securityPhoneLabel;

/**
 * "Deliver via" channels a signer can pick (one or more). Mirrors the desktop
 * row's `CHANNEL_META` — same values, labels, gating and region note — so both
 * authoring flows offer exactly the same choices.
 */
const CHANNEL_META = [
  {
    value: strings.deliveryChannels.email as DeliveryChannel,
    label: strings.smsDelivery.emailLabel,
    gated: false,
    regionNote: "",
  },
  {
    value: strings.deliveryChannels.sms as DeliveryChannel,
    label: strings.smsDelivery.smsLabel,
    gated: true,
    regionNote: strings.smsDelivery.smsRegionNote,
  },
  // WhatsApp delivery is temporarily disabled — uncomment to re-enable
  // (keep in step with the desktop row's own commented-out entry).
  // {
  //   value: strings.deliveryChannels.whatsApp as DeliveryChannel,
  //   label: strings.smsDelivery.whatsAppLabel,
  //   gated: true,
  //   regionNote: "",
  // },
] as const;

/**
 * Delivery mode per signer (in-person signing). Mirrors the desktop row's
 * `DELIVERY_META` in `components/SignerInputFields.tsx` — same values, labels
 * and captions, so both authoring flows read identically.
 */
const DELIVERY_META = [
  {
    value: strings.deliveryModes.email,
    label: strings.inPerson.deliveryEmailLabel,
    caption: strings.inPerson.deliveryEmailCaption,
  },
  {
    value: strings.deliveryModes.inPerson,
    label: strings.inPerson.deliveryInPersonLabel,
    caption: strings.inPerson.deliveryInPersonCaption,
  },
] as const;

interface MobileSignerCardProps {
  signer: RequestParticipant;
  displayIndex: number; // 0-based position across all signers
  signerOrderRequired: boolean;
  signers: RequestParticipant[];
  excludeIds: string[];
  setExcludeIds: Function;
  contactList: any;
  setContactList: Function;
  onDelayDateTime: (signer: RequestParticipant) => void;
  onDelayDays: (signer: RequestParticipant) => void;
  onAddDelay: (signer: RequestParticipant) => void;
}

const MobileSignerCard: React.FC<MobileSignerCardProps> = ({
  signer,
  displayIndex,
  signerOrderRequired,
  signers,
  excludeIds,
  setExcludeIds,
  contactList,
  setContactList,
  onDelayDateTime,
  onDelayDays,
  onAddDelay,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const errors = useAppSelector((state: RootState) => state.app.errors);
  const contacts = useAppSelector((state: RootState) => state.app.contacts);
  /** Envelope the in-person consent is recorded against. */
  const signRequestId = useAppSelector(
    (state: RootState) => state.app.signRequest?.id,
  );
  const index = displayIndex;
  const nameTimeoutRef = useRef<any>(null);
  const emailTimeoutRef = useRef<any>(null);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [roleAnchor, setRoleAnchor] = useState<null | HTMLElement>(null);
  const [securityAnchor, setSecurityAnchor] = useState<null | HTMLElement>(null);
  const [deliveryAnchor, setDeliveryAnchor] = useState<null | HTMLElement>(null);
  /**
   * Requester attestation, taken the first time an in-person signer is added
   * to the account — one-time and account-level, so a cached "yes" from
   * redux skips this dialog entirely.
   */
  const [isInPersonConsentOpen, setInPersonConsentOpen] =
    useState<boolean>(false);
  const { ensureConsentStatus, recordConsent } = useRequesterInPersonConsent();

  // An identity verification option is a security type of its own (single
  // choice); shown by its label, picked by its `IDV_SECURITY_OPTIONS` key.
  const idvKey = idvSecurityKeyOf(signer.requestSecurity);
  const securityType = idvKey
    ? idvSecurityOption(idvKey)!.label
    : signer.requestSecurity?.needSecuredAccess
    ? signer.requestSecurity?.needPhoneValidation
      ? SEC_PHONE
      : signer.requestSecurity?.needOTPValidation
        ? SEC_OTP
        : SEC_CODE
    : SEC_NONE;

  /**
   * SMS Delivery — the signing link can go by email, text (SMS) or WhatsApp.
   * An empty/undefined list means email-only (backward compatible).
   */
  const deliveryChannels: DeliveryChannel[] = signer.deliveryChannels?.length
    ? signer.deliveryChannels
    : [strings.deliveryChannels.email as DeliveryChannel];
  const hasNonEmailChannel = deliveryChannels.some(
    (channel) => channel !== strings.deliveryChannels.email,
  );

  const otpState = useFeatureState(FeatureKeys.AUTH_OTP);
  const otpDisabled = otpState.status !== strings.FEATURE_STATUS_AVAILABLE;
  const passcodeState = useFeatureState(FeatureKeys.AUTH_PASSCODE);
  const passcodeDisabled =
    passcodeState.status !== strings.FEATURE_STATUS_AVAILABLE;
  const inPersonState = useFeatureState(FeatureKeys.ENVELOPE_IN_PERSON);
  const isInPersonAvailable =
    inPersonState.status === strings.FEATURE_STATUS_AVAILABLE;

  // SMS Delivery gate — covers the SMS / WhatsApp delivery channels. Email
  // delivery is always available.
  const smsState = useFeatureState(FeatureKeys.ENVELOPE_SEND_SMS);
  const isSmsAvailable = smsState.status === strings.FEATURE_STATUS_AVAILABLE;
  const smsTooltip =
    smsState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((smsState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : smsState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  // Phone (SMS OTP) authentication is its own feature, separate from SMS
  // delivery — an auth method alongside Email OTP and Access Code.
  // Identity verification options: see useIdvSecurityOptions.
  const idvOptions = useIdvSecurityOptions(idvKey);

  const phoneAuthState = useFeatureState(FeatureKeys.AUTH_PHONE);
  const isPhoneAuthAvailable =
    phoneAuthState.status === strings.FEATURE_STATUS_AVAILABLE;
  // Anything other than an explicit IN_PERSON reads as EMAIL, matching the
  // backend default for signers saved before the field existed.
  // In the embedded wizard a signer signs in the partner's app (EMBEDDED) unless the
  // partner pre-filled them as EMAIL; see embedDeliveryModeOf. In person is never
  // offered there, so the Delivery block stays hidden.
  const isEmbed = isEmbedEditorMode();
  const deliveryMode = isEmbed
    ? embedDeliveryModeOf(signer.deliveryMode)
    : signer.deliveryMode === strings.deliveryModes.inPerson
      ? strings.deliveryModes.inPerson
      : strings.deliveryModes.email;
  const deliveryLabel =
    DELIVERY_META.find((meta) => meta.value === deliveryMode)?.label ??
    strings.inPerson.deliveryEmailLabel;

  // A signer who signs in the partner's app is sent no link by Inkfree, so Deliver via does not
  // apply to them; security (access code, one-time codes) does, inside the iframe.
  const isEmbeddedSelected = deliveryMode === strings.deliveryModes.embedded;
  // SMS Delivery — channels only apply to remote (non in-person) signers, and
  // never to a CC recipient. Same conditions as the desktop row.
  const isInPersonSelected = deliveryMode === strings.deliveryModes.inPerson;
  const showDeliverVia =
    !isEmbeddedSelected && !signer.cc && !isInPersonSelected;
  // SMS / WhatsApp need a phone; captured in its own field under the channels.
  const showInlinePhone = showDeliverVia && hasNonEmailChannel;
  const showAuthBlock = !signer.cc && securityType === SEC_PHONE;
  // Email is optional when the link is only texted, unless email OTP is on.
  const emailOptional =
    isInPersonSelected ||
    (!deliveryChannels.includes(
      strings.deliveryChannels.email as DeliveryChannel,
    ) &&
      securityType !== SEC_OTP);
  const phonePlaceholder =
    deliveryChannels.includes(
      strings.deliveryChannels.whatsApp as DeliveryChannel,
    ) &&
    !deliveryChannels.includes(strings.deliveryChannels.sms as DeliveryChannel)
      ? strings.smsDelivery.whatsAppNumberPlaceholder
      : strings.smsDelivery.phonePlaceholder;
  const canCopyDeliveryPhone =
    hasNonEmailChannel && Boolean((signer.phone || "").trim());

  const delayedRoutingState = useFeatureState(
    FeatureKeys.ENVELOPE_DELAYED_ROUTING,
  );
  const isDelayedRoutingAvailable =
    delayedRoutingState.status === strings.FEATURE_STATUS_AVAILABLE;

  const isDelayed = Boolean(
    signer.delayDays || signer.delayHours || signer.scheduleTime,
  );
  const canDelay =
    signerOrderRequired &&
    !signer.cc &&
    Number(signer.order) > 1 &&
    isDelayedRoutingAvailable;

  // ---- Drag source (reorder). Connects to the app-wide multi-backend DnD. ----
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ItemType.SIGNER,
    item: () => ({
      dragType: "SINGLE",
      id: signer.id,
      order: signer.order,
      initialOrder: signer.order,
    }),
    canDrag: () => signerOrderRequired,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  const updateSigner = (fields: Partial<RequestParticipant>) =>
    dispatch(
      updateIndividualSigner({ signerId: signer.id, fieldsToUpdate: fields }),
    );

  const handleResetContacts = () => {
    if (contacts.length < 10) {
      getContactSuggestion("", dispatch);
      dispatch(setContacts(contacts));
    }
  };

  const handleRemoveContacts = () => {
    setContactList((prev: any) =>
      Object.keys(prev).reduce((acc: any, key: string) => {
        const { [index]: _omit, ...rest } = prev[key];
        return { ...acc, [key]: rest };
      }, {}),
    );
  };

  const handleRoleSelect = (role: string) => {
    if (role === ROLE_RECEIVES_COPY) {
      updateSigner({
        cc: true,
        delayDays: undefined,
        delayHours: undefined,
        scheduleTime: undefined,
        requestSecurity: {
          needSecuredAccess: false,
          needOTPValidation: false,
          needPhoneValidation: false,
          securityCode: null,
          otp: signer.requestSecurity?.otp ?? null,
          otpValidUntil: signer.requestSecurity?.otpValidUntil ?? null,
          isVerified: signer.requestSecurity?.isVerified ?? false,
          verifiedAt: signer.requestSecurity?.verifiedAt ?? null,
          authPhone: signer.requestSecurity?.authPhone ?? null,
        },
      });
      dispatch(removeFieldsOfCC({ signerId: signer.id }));
    } else {
      updateSigner({ cc: false });
    }
    setRoleAnchor(null);
  };

  const handleSecuritySelect = (option: string) => {
    // Identity verification replaces any code or OTP: it is never combined.
    const needSecuredAccess = option !== SEC_NONE && !isIdvSecurityKey(option);
    const needOTPValidation = option === SEC_OTP;
    const needPhoneValidation = option === SEC_PHONE;
    updateSigner({
      requestSecurity: {
        needSecuredAccess,
        needOTPValidation,
        needPhoneValidation,
        securityCode:
          option === SEC_CODE ? signer.requestSecurity?.securityCode ?? null : null,
        otp: signer.requestSecurity?.otp ?? null,
        otpValidUntil: signer.requestSecurity?.otpValidUntil ?? null,
        isVerified: signer.requestSecurity?.isVerified ?? false,
        verifiedAt: signer.requestSecurity?.verifiedAt ?? null,
        authPhone: signer.requestSecurity?.authPhone ?? null,
        identityVerificationMethods: idvMethodsFor(option),
      },
    });
    setSecurityAnchor(null);
  };

  /**
   * In person there is no remote signer to send to a vendor, so a signer moved
   * there drops identity verification back to no security.
   */
  const inPersonFields = (): Partial<RequestParticipant> => ({
    deliveryMode: strings.deliveryModes.inPerson as DeliveryMode,
    ...(idvKey
      ? {
          requestSecurity: {
            ...signer.requestSecurity!,
            needSecuredAccess: false,
            identityVerificationMethods: [],
          },
        }
      : {}),
  });

  /** Merge a change into the existing security block (auth phone). */
  const patchSecurity = (partial: Partial<RequestSecurity>) => {
    const current = signer.requestSecurity;
    if (!current) return;
    updateSigner({ requestSecurity: { ...current, ...partial } });
  };

  /**
   * Toggle a delivery channel. At least one channel must stay selected, so
   * clearing the last one is a no-op (same rule as the desktop row).
   */
  const toggleChannel = (channel: DeliveryChannel) => {
    const isOn = deliveryChannels.includes(channel);
    let next = isOn
      ? deliveryChannels.filter((each) => each !== channel)
      : [...deliveryChannels, channel];
    if (!next.length) next = deliveryChannels;
    updateSigner({ deliveryChannels: next });
  };

  const handleCopyDeliveryPhone = () => {
    patchSecurity({ authPhone: signer.phone || "" });
  };

  /**
   * Switching a signer to in-person is the point the decision is made, so the
   * requester's attestation is taken here rather than when the host later
   * hands the device over. Asked only once per account — a cached "yes"
   * applies the mode straight away. The mode is only applied once they
   * confirm — backing out of the dialog leaves the signer on email delivery.
   */
  const handleDeliverySelect = async (mode: string) => {
    setDeliveryAnchor(null);
    if (
      mode === strings.deliveryModes.inPerson &&
      deliveryMode !== strings.deliveryModes.inPerson
    ) {
      const alreadyConsented = await ensureConsentStatus();
      if (alreadyConsented) {
        updateSigner(inPersonFields());
        return;
      }
      setInPersonConsentOpen(true);
      return;
    }
    updateSigner({ deliveryMode: mode as DeliveryMode });
  };

  const handleSecurityCodeChange = (value: string) => {
    updateSigner({
      requestSecurity: {
        needSecuredAccess: true,
        needOTPValidation: false,
        needPhoneValidation: false,
        securityCode: value || null,
        otp: signer.requestSecurity?.otp ?? null,
        otpValidUntil: signer.requestSecurity?.otpValidUntil ?? null,
        isVerified: signer.requestSecurity?.isVerified ?? false,
        verifiedAt: signer.requestSecurity?.verifiedAt ?? null,
        authPhone: signer.requestSecurity?.authPhone ?? null,
      },
    });
  };

  const handleDelete = () => {
    dispatch(deleteSigner({ signerId: signer.id }));
    dispatch(removeFieldsOfCC({ signerId: signer.id }));
    if (signer.email) {
      setExcludeIds(excludeIds.filter((id: string) => id !== signer.email));
    }
    if (!isTruthy(signer.name) || !isTruthy(signer.email)) {
      dispatch(setErrors({ ...errors, name: "", email: "" }));
    }
    setMenuAnchor(null);
  };

  const isEmptyRow = !isTruthy(signer.name) && !isTruthy(signer.email);

  return (
    <>
      {/* Delay section — rendered ABOVE the card (Figma places it between cards). */}
      {isDelayed ? (
        <Box sx={{ ...classes.delayChip, mb: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={classes.delayChipLabel}>Delay by</Typography>
            <Typography sx={classes.delayChipValue}>
              {signer.scheduleTime
                ? convertESTtoUserLocalDateAndTime(
                    signer.scheduleTime,
                    "MMM D, YYYY h:mm A",
                    "MM/DD/YYYY HH:mm:ss",
                  )
                : `${signer.delayDays ?? 0} days ${signer.delayHours ?? 0} hours`}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
            <IconButton
              size="small"
              aria-label="Edit delay"
              sx={{ p: 0, color: m.base[500] }}
              onClick={() =>
                signer.scheduleTime
                  ? onDelayDateTime(signer)
                  : onDelayDays(signer)
              }
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Remove delay"
              sx={{ p: 0, color: m.base[500] }}
              onClick={() =>
                updateSigner({
                  delayDays: undefined,
                  delayHours: undefined,
                  scheduleTime: undefined,
                })
              }
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      ) : canDelay ? (
        <Box sx={{ mb: 1.5 }}>
          <Box
            component="span"
            sx={classes.addDelayLink}
            {...pressableProps(() => onAddDelay(signer), { label: "Add delay" })}
          >
            <AccessTimeIcon sx={{ fontSize: 16 }} />
            Add Delay
          </Box>
        </Box>
      ) : null}

      <Box sx={{ ...classes.signerCard, opacity: isDragging ? 0.4 : 1 }}>
        {/* Header: badge + title + 3-dot */}
        <Box sx={classes.signerCardHead}>
          <Box
            ref={(node: any) => {
              if (signerOrderRequired) drag(node);
            }}
            sx={{
              ...classes.signerCardHeadLeft,
              ...(signerOrderRequired && {
                cursor: "grab",
                touchAction: "none",
              }),
            }}
          >
            <Box
              aria-hidden="true"
              sx={classes.signerBadge(signer.color || m.ePurple[500])}
            >
              {displayIndex + 1}
            </Box>
            <Typography sx={classes.signerCardTitle}>
              Signers {displayIndex + 1}
            </Typography>
          </Box>

        <IconButton
          size="small"
          aria-label={`Options for signer ${displayIndex + 1}`}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          aria-controls={
            menuAnchor ? `signer-${signer.id}-menu` : undefined
          }
          sx={{ p: 0.5, color: m.base[500] }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id={`signer-${signer.id}-menu`}
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ "aria-label": "Signer options" }}
        >
          <MenuItem
            disabled={!canDelay || isDelayed}
            onClick={() => {
              onDelayDateTime(signer);
              setMenuAnchor(null);
            }}
          >
            <Typography variant="subtitle2">Delay by date and time</Typography>
          </MenuItem>
          <MenuItem
            disabled={!canDelay || isDelayed}
            onClick={() => {
              onDelayDays(signer);
              setMenuAnchor(null);
            }}
          >
            <Typography variant="subtitle2">Delay by days</Typography>
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <Typography variant="subtitle2">Delete</Typography>
          </MenuItem>
        </Menu>
      </Box>

      {/* Full name (desktop autocomplete with contact suggestions) */}
      <Typography
        component="label"
        htmlFor={`signer-${signer.id}-name`}
        sx={classes.fieldLabel}
      >
        Full Name
      </Typography>
      <CustomAutoComplete
        id={`signer-${signer.id}-name`}
        placeHolder={
          deliveryMode === strings.deliveryModes.inPerson
            ? "Name (optional)"
            : "Enter full name"
        }
        name="name"
        freeSolo
        textFieldVariant="outlined"
        value={signer.name || ""}
        sx={classes.textInput}
        onBlur={handleResetContacts}
        onChange={(_e: any, newValue: any) => {
          if (newValue && typeof newValue === "object") {
            const updatedExcludeIds = [
              ...excludeIds.filter((id) => id !== signer.email),
              newValue.email,
            ];
            setExcludeIds(updatedExcludeIds);
            updateSigner({
              name: newValue.name.substring(0, 200) || "",
              email:
                newValue.email.substring(0, 100) ||
                signer.email.substring(0, 100),
              // A contact's saved number seeds the SMS delivery phone, as on
              // desktop — kept only if the contact has one.
              phone: newValue.phone
                ? newValue.phone.substring(0, 50)
                : signer.phone || "",
            });
            getContactSuggestion(" ", dispatch, updatedExcludeIds);
          } else if (typeof newValue === "string") {
            updateSigner({
              name: newValue
                .trimStart()
                .replace(/(?:^\s+|\s+$|\t+)/g, " ")
                .substring(0, 200),
            });
          } else if (newValue === null || newValue === "") {
            updateSigner({ name: "" });
          }
          dispatch(setErrors({ ...errors, name: "" }));
        }}
        onChangeForTextField={(e: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          updateSigner({
            name: value
              .trimStart()
              .replace(/(?:^\s+|\s+$|\t+)/g, " ")
              .substring(0, 200),
          });
          dispatch(setErrors({ ...errors, name: "" }));
          clearTimeout(nameTimeoutRef.current);
          nameTimeoutRef.current = setTimeout(async () => {
            if (isTruthy(value.trim())) {
              dispatch(setContacts([]));
              await getContactSuggestion(
                value.trim(),
                dispatch,
                excludeIds,
                setContactList,
                index,
                "name",
              );
            } else {
              await getContactSuggestion(
                "",
                dispatch,
                excludeIds,
                setContactList,
                index,
                "name",
              );
              dispatch(setContacts(contacts));
            }
          }, 500);
        }}
        filterOptions={(options: any, { inputValue }: any) =>
          options.filter(
            (option: any) =>
              option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.email.toLowerCase().includes(inputValue.toLowerCase()),
          )
        }
        ref={nameTimeoutRef}
        options={(contactList.name && contactList.name[index]
          ? contactList.name[index]
          : contacts || []
        )?.filter(
          (c: any) =>
            !excludeIds
              .map((id: string) => id.toLowerCase())
              .includes(c.email.toLowerCase()),
        )}
        getOptionLabel={(option: any) =>
          typeof option === "string" ? option : option.name || ""
        }
        renderOption={(prop: any, option: any) => (
          <li {...prop}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {option.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "grey" }}>
                {option.email}
              </Typography>
            </Box>
          </li>
        )}
        error={
          !(
            isTruthy(signer.name) && isValidParticipantNameLength(signer.name)
          ) &&
          // A blank in-person placeholder is allowed — the host captures the
          // name at hand-off — so don't flag it.
          !(
            deliveryMode === strings.deliveryModes.inPerson &&
            !isTruthy(signer.name)
          ) &&
          errors.name
        }
        errorMessage={
          !isTruthy(signer.name) && errors.name
            ? "Name is required"
            : getParticipantNameError(signer.name) || ""
        }
      />

      {/* Email (desktop autocomplete with contact suggestions) */}
      <Typography
        component="label"
        htmlFor={`signer-${signer.id}-email`}
        sx={classes.fieldLabel}
      >
        Email Address
      </Typography>
      <CustomAutoComplete
        id={`signer-${signer.id}-email`}
        placeHolder={
          emailOptional ? "Email (optional)" : "Enter email address"
        }
        name="email"
        freeSolo
        textFieldVariant="outlined"
        value={signer.email || ""}
        sx={classes.textInput}
        onBlur={handleResetContacts}
        onChange={(_e: any, newValue: any) => {
          if (newValue && typeof newValue === "object") {
            const updatedExcludeIds = [
              ...excludeIds.filter((id) => id !== signer.email),
              newValue.email,
            ];
            setExcludeIds(updatedExcludeIds);
            updateSigner({
              name:
                newValue.name.substring(0, 200) ||
                signer.name.substring(0, 200),
              email: newValue.email.substring(0, 256) || "",
              phone: newValue.phone
                ? newValue.phone.substring(0, 50)
                : signer.phone || "",
            });
            getContactSuggestion(" ", dispatch, updatedExcludeIds);
          } else if (typeof newValue === "string") {
            updateSigner({ email: newValue.replace(/\s/g, "").substring(0, 256) });
          } else if (newValue === null || newValue === "") {
            updateSigner({ email: "" });
          }
          dispatch(setErrors({ ...errors, email: "" }));
          handleRemoveContacts();
        }}
        onChangeForTextField={(e: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          updateSigner({ email: value.replace(/\s/g, "").substring(0, 256) });
          dispatch(setErrors({ ...errors, email: "" }));
          clearTimeout(emailTimeoutRef.current);
          emailTimeoutRef.current = setTimeout(() => {
            if (isTruthy(value.trim())) {
              getContactSuggestion(
                value.trim(),
                dispatch,
                excludeIds,
                setContactList,
                index,
                "email",
              );
            } else {
              dispatch(setContacts([]));
            }
          }, 500);
        }}
        filterOptions={(options: any, { inputValue }: any) =>
          options.filter(
            (option: any) =>
              option.email.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.name.toLowerCase().includes(inputValue.toLowerCase()),
          )
        }
        ref={emailTimeoutRef}
        options={(contactList.email && contactList.email[index]
          ? contactList.email[index]
          : contacts || []
        )?.filter(
          (c: any) =>
            !excludeIds
              .map((id: string) => id.toLowerCase())
              .includes(c.email.toLowerCase()),
        )}
        getOptionLabel={(option: any) =>
          typeof option === "string" ? option : option.email || ""
        }
        renderOption={(prop: any, option: any) => (
          <li {...prop}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {option.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "grey" }}>
                {option.email}
              </Typography>
            </Box>
          </li>
        )}
        error={
          (!isTruthy(signer.email) || !strings.regex.test(signer.email)) &&
          // A blank email is allowed where it's optional — an in-person
          // placeholder, or a link that only goes out by text.
          !(emailOptional && !isTruthy(signer.email)) &&
          errors.email
        }
      />

      {/* Role + Security */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={classes.fieldLabel}>Role</Typography>
          <Box
            sx={classes.dropdownField}
            {...pressableProps((e) => setRoleAnchor(e.currentTarget as HTMLElement), {
              label: `Role, ${
                isEmptyRow
                  ? "not selected"
                  : signer.cc
                    ? ROLE_RECEIVES_COPY
                    : ROLE_NEED_TO_SIGN
              }`,
              hasPopup: "menu",
              expanded: Boolean(roleAnchor),
            })}
          >
            <Typography sx={classes.dropdownFieldText(isEmptyRow)}>
              {isEmptyRow
                ? "Select role"
                : signer.cc
                  ? ROLE_RECEIVES_COPY
                  : ROLE_NEED_TO_SIGN}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: m.grey[400] }} />
          </Box>
          <Menu
            anchorEl={roleAnchor}
            open={Boolean(roleAnchor)}
            onClose={() => setRoleAnchor(null)}
            MenuListProps={{ "aria-label": "Role" }}
          >
            <MenuItem onClick={() => handleRoleSelect(ROLE_NEED_TO_SIGN)}>
              <Typography variant="subtitle2">{ROLE_NEED_TO_SIGN}</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleRoleSelect(ROLE_RECEIVES_COPY)}>
              <Typography variant="subtitle2">{ROLE_RECEIVES_COPY}</Typography>
            </MenuItem>
          </Menu>
        </Box>

        {!signer.cc && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={classes.fieldLabel}>Security</Typography>
            <Box
              sx={classes.dropdownField}
              {...pressableProps(
                (e) => setSecurityAnchor(e.currentTarget as HTMLElement),
                {
                  label: `Security, ${isEmptyRow ? "not selected" : securityType}`,
                  hasPopup: "menu",
                  expanded: Boolean(securityAnchor),
                },
              )}
            >
              <Typography sx={classes.dropdownFieldText(isEmptyRow)}>
                {isEmptyRow ? "Select security" : securityType}
              </Typography>
              <KeyboardArrowDownIcon
                sx={{ fontSize: 18, color: m.grey[400] }}
              />
            </Box>
            <Menu
              anchorEl={securityAnchor}
              open={Boolean(securityAnchor)}
              onClose={() => setSecurityAnchor(null)}
              MenuListProps={{ "aria-label": "Security" }}
            >
              <MenuItem onClick={() => handleSecuritySelect(SEC_NONE)}>
                <Typography variant="subtitle2">{SEC_NONE}</Typography>
              </MenuItem>
              <MenuItem
                disabled={passcodeDisabled}
                onClick={() => handleSecuritySelect(SEC_CODE)}
              >
                <Typography variant="subtitle2">{SEC_CODE}</Typography>
                {passcodeDisabled && (
                  <LockOutlinedIcon
                    sx={{ fontSize: 14, color: m.grey[400], ml: "auto" }}
                  />
                )}
              </MenuItem>
              <MenuItem
                disabled={otpDisabled}
                onClick={() => handleSecuritySelect(SEC_OTP)}
              >
                <Typography variant="subtitle2">{SEC_OTP}</Typography>
                {otpDisabled && (
                  <LockOutlinedIcon
                    sx={{ fontSize: 14, color: m.grey[400], ml: "auto" }}
                  />
                )}
              </MenuItem>
              {/* SMS OTP — phone authentication, its own feature (AUTH_PHONE)
                  separate from SMS delivery. */}
              <MenuItem
                disabled={!isPhoneAuthAvailable}
                onClick={() => handleSecuritySelect(SEC_PHONE)}
              >
                <Typography variant="subtitle2">{SEC_PHONE}</Typography>
                {!isPhoneAuthAvailable && (
                  <LockOutlinedIcon
                    sx={{ fontSize: 14, color: m.grey[400], ml: "auto" }}
                  />
                )}
              </MenuItem>
              {/* Identity verification — once the account has it, or when this
                  signer already uses it. Not for in-person signing. */}
              {idvOptions.map((option) => (
                  <MenuItem
                    key={option.key}
                    selected={idvKey === option.key}
                    disabled={isInPersonSelected}
                    onClick={() => handleSecuritySelect(option.key)}
                  >
                    <Typography variant="subtitle2">{option.label}</Typography>
                  </MenuItem>
                ))}
            </Menu>
          </Box>
        )}
      </Box>

      {/* Delivery mode (in-person signing) — desktop parity, own row so the
          Role/Security pair keeps its width on a phone. */}
      {!signer.cc && isInPersonAvailable && !isEmbed && (
        // Flex column for the same reason the Role/Security columns are: it keeps
        // `fieldLabel`'s top margin *inside* the block instead of letting it
        // collapse through a bare wrapper, so Delivery is spaced exactly like
        // every other field rather than needing padding of its own.
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={classes.fieldLabel}>Delivery</Typography>
          <Box
            sx={classes.dropdownField}
            {...pressableProps(
              (e) => setDeliveryAnchor(e.currentTarget as HTMLElement),
              {
                label: `Delivery, ${deliveryLabel}`,
                hasPopup: "menu",
                expanded: Boolean(deliveryAnchor),
              },
            )}
          >
            <Typography sx={classes.dropdownFieldText(isEmptyRow)}>
              {deliveryLabel}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: m.grey[400] }} />
          </Box>
          <Menu
            anchorEl={deliveryAnchor}
            open={Boolean(deliveryAnchor)}
            onClose={() => setDeliveryAnchor(null)}
            MenuListProps={{ "aria-label": "Delivery" }}
          >
            {DELIVERY_META.map((meta) => (
              <MenuItem
                key={meta.value}
                selected={deliveryMode === meta.value}
                onClick={() => handleDeliverySelect(meta.value)}
                sx={{ display: "block" }}
              >
                <Typography variant="subtitle2">{meta.label}</Typography>
                <Typography variant="caption" sx={{ color: m.grey[400] }}>
                  {meta.caption}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
          {/* Records the requester's one-time, account-level consent on
              confirm, so no later hand-off or signer ever asks again. */}
          <InPersonHandoffDialog
            open={isInPersonConsentOpen}
            requestId={String(signRequestId ?? "")}
            variant="authoring"
            onRecordConsent={recordConsent}
            onCancel={() => setInPersonConsentOpen(false)}
            onSubmit={() => {
              updateSigner(inPersonFields());
              setInPersonConsentOpen(false);
            }}
          />
        </Box>
      )}

      {/* Deliver via (SMS delivery) — desktop parity, stacked for a phone. */}
      {showDeliverVia && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={classes.fieldLabel}>
            {strings.smsDelivery.deliverViaLabel}
          </Typography>
          <Box sx={classes.deliverViaGroup}>
            {CHANNEL_META.map((channel) => {
              const on = deliveryChannels.includes(channel.value);
              const locked = channel.gated && !isSmsAvailable;
              return (
                <Button
                  key={channel.value}
                  disableRipple
                  disabled={locked}
                  aria-pressed={on}
                  onClick={() => toggleChannel(channel.value)}
                  sx={classes.deliverOption}
                >
                  <Box component="span" sx={classes.deliverBox(on)}>
                    <TickGlyph sx={classes.deliverTick(on)} />
                  </Box>
                  <Box component="span" sx={classes.deliverOptionLabel}>
                    {channel.label}
                  </Box>
                  {channel.regionNote && (
                    <Box component="span" sx={classes.deliverOptionNote}>
                      {channel.regionNote}
                    </Box>
                  )}
                  {locked && <LockOutlinedIcon sx={classes.deliverLock} />}
                </Button>
              );
            })}
          </Box>
          {!isSmsAvailable && smsTooltip && (
            <Typography component="span" sx={classes.deliverGateNote}>
              {smsTooltip}
            </Typography>
          )}
        </Box>
      )}

      {/* Delivery phone — required for any non-email channel. */}
      {showInlinePhone && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            component="label"
            htmlFor={`signer-${signer.id}-phone`}
            sx={classes.fieldLabel}
          >
            {strings.smsDelivery.phoneNumberLabel}
          </Typography>
          <CustomContactNumberInput
            id={`signer-${signer.id}-phone`}
            placeHolder={phonePlaceholder}
            value={signer.phone || ""}
            countries={strings.SMS_SUPPORTED_COUNTRIES}
            sx={classes.phoneField}
            onChange={(value: string) => updateSigner({ phone: value })}
          />
        </Box>
      )}

      {/* Security code input */}
      {!signer.cc && securityType === SEC_CODE && (
        <MobileTextField
          label="Security Code"
          placeholder="Enter security code"
          value={signer.requestSecurity?.securityCode || ""}
          onChange={(e) => handleSecurityCodeChange(e.target.value)}
          error={Boolean(errors.securityCode)}
        />
      )}

      {/* Phone authentication (SMS OTP) — the number the code is texted to. */}
      {showAuthBlock && (
        <Box sx={classes.authBlock}>
          <Box sx={classes.authBlockHead}>
            <ShieldGlyph sx={{ width: 16, height: 16, color: m.ePurple[500] }} />
            <Typography component="span" sx={classes.authTitle}>
              {strings.smsDelivery.authTitle}
            </Typography>
          </Box>
          <Typography component="span" sx={classes.authNote}>
            {`${strings.smsDelivery.smsRegionNote} — ${strings.smsDelivery.authNote}`}
          </Typography>
          <CustomContactNumberInput
            id={`signer-${signer.id}-auth-phone`}
            placeHolder={strings.smsDelivery.authPlaceholder}
            value={signer.requestSecurity?.authPhone || ""}
            countries={strings.SMS_SUPPORTED_COUNTRIES}
            sx={classes.phoneField}
            onChange={(value: string) => patchSecurity({ authPhone: value })}
          />
          {canCopyDeliveryPhone && (
            <Button
              disableRipple
              onClick={handleCopyDeliveryPhone}
              sx={classes.sameAsDelivery}
            >
              <CopyGlyph sx={{ width: 14, height: 14 }} />
              {strings.smsDelivery.sameAsDelivery}
            </Button>
          )}
        </Box>
      )}

      </Box>
    </>
  );
};

export default MobileSignerCard;
